<template>
  <section class="hero-section">
    <div class="hero-container">

      <!-- Left Content -->
      <div class="hero-content">
        <div class="product-badge">
          <span class="highlight">Stardust</span> Cookie Cutter
        </div>

        <h1 class="hero-headline">
          Never click another cookie banner again
        </h1>

        <p class="hero-subheadline">
          Set your privacy preferences once. Browse the web without constant interruptions.
          Free, open-source, and privacy-first.
        </p>

        <!-- Trust Badges -->
        <div class="trust-badges">
          <div class="badge">
            <svg class="badge-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              <path d="M9 12l2 2 4-4"/>
            </svg>
            <span>100% Free</span>
          </div>
          <div class="badge">
            <svg class="badge-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"/>
              <path d="M12 6v6l4 2"/>
            </svg>
            <span>Open Source</span>
          </div>
          <div class="badge">
            <svg class="badge-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
            <span>Privacy-First</span>
          </div>
        </div>

        <!-- Primary CTA -->
        <div class="cta-section">
          <button
            v-if="primaryBrowser"
            class="btn-primary-cta"
            @click="openURL(primaryBrowser.url)"
            :aria-label="`Install Stardust Cookie Cutter for ${primaryBrowser.name}`">
            <img :src="require(`../../assets/browserLogos/${primaryBrowser.img}`)" :alt="primaryBrowser.name" class="cta-browser-icon">
            <div class="cta-text">
              <span class="cta-main">Add to {{ primaryBrowser.shortName }}</span>
              <span class="cta-sub">Free • Takes 30 seconds</span>
            </div>
            <svg class="cta-arrow" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
          </button>

          <div class="browser-dropdown-wrapper">
            <button class="btn-other-browsers" @click="showAllBrowsers = !showAllBrowsers" :aria-expanded="showAllBrowsers.toString()">
              {{ showAllBrowsers ? 'Hide' : 'Other browsers' }}
              <svg class="dropdown-icon" :class="{ 'is-open': showAllBrowsers }" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="6 9 12 15 18 9"/>
              </svg>
            </button>

            <!-- All Browsers Dropdown -->
            <transition name="slide-fade">
              <div v-if="showAllBrowsers" class="all-browsers">
                <button
                  v-for="browser in otherBrowsers"
                  :key="browser.id"
                  class="btn-browser-option"
                  @click="openURL(browser.url)"
                  :aria-label="`Install Stardust Cookie Cutter for ${browser.name}`">
                  <img :src="require(`../../assets/browserLogos/${browser.img}`)" :alt="browser.name" class="browser-option-icon">
                  <div class="browser-option-text">
                    <span class="browser-option-main">Add to {{ browser.shortName }}</span>
                    <span class="browser-option-sub">Free • Takes 30 seconds</span>
                  </div>
                  <svg class="browser-option-arrow" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M5 12h14M12 5l7 7-7 7"/>
                  </svg>
                </button>
              </div>
            </transition>
          </div>
        </div>

        <!-- Social Proof -->
        <div class="social-proof">
          <div class="stat">
            <strong>10,000+</strong> active users
          </div>
          <div class="stat-divider">•</div>
          <div class="stat">
            <strong>4.1★</strong> average rating
          </div>
          <div class="stat-divider">•</div>
          <div class="stat">
            <strong>6</strong> browsers supported
          </div>
        </div>
      </div>

      <!-- Right Visual -->
      <div class="hero-visual">
        <Illustration1 />
      </div>

    </div>
  </section>
</template>

<script>
import Illustration1 from "./Illustration1.vue";

export default {
  components: {
    Illustration1
  },

  data() {
    return {
      showAllBrowsers: false
    };
  },

  methods: {
    openURL(url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    },
  },

  computed: {
    browsers() {
      // Detect browser and set properties
      const allBrowsers = [
        {
          name: "Google Chrome",
          shortName: "Chrome",
          img: "chrome.png",
          video: "dSanh8_jJeQ",
          url: "https://chrome.google.com/webstore/detail/stardust-cookie-cutter/knliaomflpbgdhnlhomjmmoahdfhnhae",
          id: 1,
          userAgentMatch: /Chrome/i,
          priority: 1
        },
        {
          name: "Apple Safari",
          shortName: "Safari",
          img: "safari.png",
          video: "uLJJqLGZxyI",
          url: "https://apps.apple.com/us/app/stardust-cookie-cutter/id1567837223",
          id: 2,
          userAgentMatch: /Safari/i,
          priority: 2
        },
        {
          name: "Mozilla Firefox",
          shortName: "Firefox",
          img: "firefox.png",
          video: "fhQQ6UTJTR8",
          url: "https://addons.mozilla.org/addon/stardust-cookie-cutter/",
          id: 3,
          userAgentMatch: /Firefox/i,
          priority: 3
        },
        {
          name: "Microsoft Edge",
          shortName: "Edge",
          img: "edge.png",
          video: "WwylbC9CJik",
          url: "https://microsoftedge.microsoft.com/addons/detail/stardust-cookie-cutter/jhoioiejafmmjpcegggmmnaefcogejdi",
          id: 4,
          userAgentMatch: /Edg/i,
          priority: 4
        },
        {
          name: "Brave Browser",
          shortName: "Brave",
          img: "brave.png",
          video: "uBbeme2loI",
          url: "https://chrome.google.com/webstore/detail/stardust-cookie-cutter/knliaomflpbgdhnlhomjmmoahdfhnhae",
          id: 5,
          userAgentMatch: /Brave/i,
          priority: 5
        },
        {
          name: "Opera Browser",
          shortName: "Opera",
          img: "opera.png",
          video: "Dr9zYhY9KFo",
          url: "https://chrome.google.com/webstore/detail/stardust-cookie-cutter/knliaomflpbgdhnlhomjmmoahdfhnhae",
          id: 6,
          userAgentMatch: /OPR/i,
          priority: 6
        },
      ];

      return allBrowsers;
    },

    primaryBrowser() {
      const userAgent = window.navigator.userAgent;

      // Check for mobile Safari/iOS first
      if (userAgent.match(/iPad/i) || userAgent.match(/iPhone/i)) {
        return this.browsers.find(b => b.id === 2); // Safari
      }

      // Check for Edge before Chrome (Edge contains Chrome in UA)
      if (userAgent.match(/Edg/i)) {
        return this.browsers.find(b => b.id === 4); // Edge
      }

      // Check for Opera before Chrome
      if (userAgent.match(/OPR/i)) {
        return this.browsers.find(b => b.id === 6); // Opera
      }

      // Check for Brave (might need additional detection)
      if (userAgent.match(/Brave/i)) {
        return this.browsers.find(b => b.id === 5); // Brave
      }

      // Check for Firefox
      if (userAgent.match(/Firefox/i)) {
        return this.browsers.find(b => b.id === 3); // Firefox
      }

      // Check for Safari (but not Chrome/Chromium)
      if (userAgent.match(/Safari/i) && !userAgent.match(/Chrome/i)) {
        return this.browsers.find(b => b.id === 2); // Safari
      }

      // Default to Chrome
      return this.browsers.find(b => b.id === 1); // Chrome
    },

    otherBrowsers() {
      return this.browsers.filter(b => b.id !== this.primaryBrowser.id);
    }
  }
}
</script>

<style scoped>
.hero-section {
  background-color: var(--color-charcoal-mist-70);
  backdrop-filter: var(--blur-glassmorphism);
  filter: var(--shadow-card);
  padding: var(--spacing-3xl) var(--container-padding);
  margin-bottom: 0;
  position: relative;
  z-index: 10;
}

.hero-container {
  max-width: var(--container-max-width);
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-3xl);
}

/* Left Content */
.hero-content {
  flex: 1;
  max-width: 600px;
}

.product-badge {
  display: inline-block;
  font-size: 16px;
  font-weight: var(--font-weight-medium);
  color: var(--color-pure-white);
  background: var(--color-gold-glow-15);
  padding: var(--spacing-xs) var(--spacing-md);
  border-radius: var(--radius-pill);
  border: 1px solid var(--color-gold-glow-30);
  margin-bottom: var(--spacing-lg);
}

.hero-headline {
  font-size: clamp(36px, 5vw, 56px);
  line-height: 1.2;
  color: var(--color-pure-white);
  margin: 0 0 var(--container-padding) 0;
  font-weight: var(--font-weight-bold);
  letter-spacing: -0.01em;
  text-align: left;
}

.hero-subheadline {
  font-size: clamp(16px, 1.8vw, 18px);
  line-height: 1.7;
  color: var(--color-soft-white);
  margin: 0 0 var(--spacing-xl) 0;
  font-weight: var(--font-weight-regular);
  text-align: left;
  font-family: var(--font-secondary);
}

/* Trust Badges */
.trust-badges {
  display: flex;
  gap: var(--spacing-md);
  margin-bottom: var(--spacing-xl);
  flex-wrap: wrap;
}

.badge {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  padding: var(--spacing-xs) 14px;
  background: rgba(254, 254, 254, 0.08);
  border-radius: var(--radius-subtle);
  font-size: 14px;
  color: var(--color-pure-white);
  font-weight: var(--font-weight-medium);
  border: 1px solid var(--color-border-subtle);
}

.badge-icon {
  width: 18px;
  height: 18px;
  color: var(--color-stardust-gold);
}

/* CTA Section */
.cta-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 24px;
  position: relative;
}

.browser-dropdown-wrapper {
  position: relative;
  width: 100%;
}

.btn-primary-cta {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 18px 28px;
  background: linear-gradient(135deg, #e8ae1b 0%, #f3c14c 100%);
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 20px rgba(232, 174, 27, 0.3);
  position: relative;
  overflow: hidden;
}

.btn-primary-cta::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0) 100%);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.btn-primary-cta:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 30px rgba(232, 174, 27, 0.4);
}

.btn-primary-cta:hover::before {
  opacity: 1;
}

.btn-primary-cta:active {
  transform: translateY(0);
}

.cta-browser-icon {
  width: 32px;
  height: 32px;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
}

.cta-text {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
  text-align: left;
}

.cta-main {
  font-size: 18px;
  font-weight: 700;
  color: #28363c;
  line-height: 1;
  margin-bottom: 4px;
}

.cta-sub {
  font-size: 13px;
  font-weight: 500;
  color: rgba(40, 54, 60, 0.7);
  line-height: 1;
}

.cta-arrow {
  width: 20px;
  height: 20px;
  color: #28363c;
  transition: transform 0.3s ease;
}

.btn-primary-cta:hover .cta-arrow {
  transform: translateX(4px);
}

.btn-other-browsers {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 20px;
  background: transparent;
  border: 1px solid rgba(254, 254, 254, 0.2);
  border-radius: 10px;
  color: #fefefe;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-other-browsers:hover {
  background: rgba(254, 254, 254, 0.05);
  border-color: rgba(254, 254, 254, 0.3);
}

.dropdown-icon {
  width: 16px;
  height: 16px;
  transition: transform 0.3s ease;
}

.dropdown-icon.is-open {
  transform: rotate(180deg);
}

/* Social Proof */
.social-proof {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 14px;
  color: rgba(254, 254, 254, 0.7);
  flex-wrap: wrap;
}

.stat {
  display: flex;
  align-items: center;
  gap: 4px;
}

.stat strong {
  color: #e8ae1b;
  font-weight: 600;
}

.stat-divider {
  color: rgba(254, 254, 254, 0.3);
}

/* All Browsers Dropdown */
.all-browsers {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  z-index: 1000;
  padding: 16px;
  background: rgba(40, 54, 60, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  border: 1px solid rgba(232, 174, 27, 0.3);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.btn-browser-option {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 14px 20px;
  background: rgba(232, 174, 27, 0.15);
  border: 1px solid rgba(232, 174, 27, 0.3);
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  width: 100%;
}

.btn-browser-option::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(232, 174, 27, 0.15) 0%, rgba(232, 174, 27, 0) 100%);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.btn-browser-option:hover {
  transform: translateY(-2px);
  background: rgba(232, 174, 27, 0.25);
  border-color: rgba(232, 174, 27, 0.5);
  box-shadow: 0 4px 16px rgba(232, 174, 27, 0.2);
}

.btn-browser-option:hover::before {
  opacity: 1;
}

.btn-browser-option:active {
  transform: translateY(0);
}

.browser-option-icon {
  width: 28px;
  height: 28px;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
}

.browser-option-text {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
  text-align: left;
}

.browser-option-main {
  font-size: 16px;
  font-weight: 600;
  color: #fefefe;
  line-height: 1;
  margin-bottom: 3px;
}

.browser-option-sub {
  font-size: 12px;
  font-weight: 500;
  color: rgba(254, 254, 254, 0.6);
  line-height: 1;
}

.browser-option-arrow {
  width: 18px;
  height: 18px;
  color: #e8ae1b;
  transition: transform 0.3s ease;
}

.btn-browser-option:hover .browser-option-arrow {
  transform: translateX(4px);
}

/* Slide fade transition */
.slide-fade-enter-active {
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-fade-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.6, 1);
}

.slide-fade-enter-from {
  transform: translateY(-15px);
  opacity: 0;
}

.slide-fade-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}

/* Right Visual */
.hero-visual {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Responsive Design */
@media (max-width: 1024px) {
  .hero-container {
    gap: 40px;
  }

  .hero-visual {
    transform: scale(0.9);
  }
}

@media (max-width: 768px) {
  .hero-section {
    padding: 40px 20px;
  }

  .hero-container {
    flex-direction: column-reverse;
    gap: 40px;
  }

  .hero-content {
    max-width: 100%;
  }

  .hero-visual {
    transform: scale(0.85);
  }

  .hero-headline,
  .hero-subheadline {
    text-align: left;
  }

  .trust-badges {
    justify-content: flex-start;
  }

  .social-proof {
    justify-content: flex-start;
  }
}

@media (max-width: 500px) {
  .hero-section {
    padding: 30px 16px;
  }

  .product-badge {
    font-size: 14px;
    padding: 6px 12px;
  }

  .trust-badges {
    gap: 8px;
  }

  .badge {
    font-size: 12px;
    padding: 6px 10px;
  }

  .btn-primary-cta {
    padding: 16px 20px;
  }

  .cta-main {
    font-size: 16px;
  }

  .cta-sub {
    font-size: 12px;
  }

  .hero-visual {
    transform: scale(0.7);
  }
}
</style>
